import { ChangeDetectionStrategy, booleanAttribute, Component, computed, inject, input } from "@angular/core";
import { IBadgeComponent, IBadgeVariants, IIcon, ITooltipDirective } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccountBidderStatus } from "@m19-board/services/account-settings.service";

interface BadgeConfig {
  label: string;
  color: IBadgeVariants["color"];
  variant: IBadgeVariants["variant"];
  icon?: IIcon;
  tooltip?: string;
}

@Component({
  selector: "app-account-status-badge",
  imports: [IBadgeComponent, TranslocoDirective, ITooltipDirective],
  templateUrl: "./account-status-badge.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountStatusBadgeComponent {
  private readonly translocoService = inject(TranslocoService);

  accountBidderStatus = input.required<AccountBidderStatus>();
  noTooltip = input(false, { transform: booleanAttribute });

  readonly badgeConfig = computed(() => {
    switch (this.accountBidderStatus()) {
      case AccountBidderStatus.FULL_AUTOMATION:
        return this.getBidderOnConfig();
      case AccountBidderStatus.FULL_AUTOMATION_WITH_DSP:
        return this.getActiveBidderOnWithDspConfig();
      case AccountBidderStatus.PAYMENT_ISSUE:
        return this.getPaymentIssueConfig();
      case AccountBidderStatus.MANUAL_SETUP_REQUIRED:
        return this.getManualSetupRequiredConfig();
      case AccountBidderStatus.PULL_STATS:
        return this.getActivePullStatsConfig();
      default:
        return this.getAdvertisingAccessLostConfig();
    }
  });

  private getBidderOnConfig(): BadgeConfig {
    return {
      label: this.translocoService.translate("account-setting.active_full_automation"),
      color: "green",
      variant: "soft",
    };
  }

  private getAdvertisingAccessLostConfig(): BadgeConfig {
    return {
      label: this.translocoService.translate("account-setting.advertising_access_lost"),
      color: "red",
      variant: "soft",
      icon: "alertTriangle",
    };
  }
  private getActiveBidderOnWithDspConfig(): BadgeConfig {
    return {
      label: this.translocoService.translate("account-setting.active_full_automation_with_dsp"),
      color: "green",
      variant: "soft",
    };
  }

  private getPaymentIssueConfig(): BadgeConfig {
    return {
      label: this.translocoService.translate("account-setting.payment_issue"),
      color: "red",
      variant: "soft",
      icon: "alertTriangle",
    };
  }
  private getManualSetupRequiredConfig(): BadgeConfig {
    return {
      label: this.translocoService.translate("account-setting.manual_setup_required"),
      color: "orange",
      variant: "soft",
      icon: "alertTriangle",
      tooltip: this.translocoService.translate("account-setting.unable_automate_tooltip"),
    };
  }
  private getActivePullStatsConfig(): BadgeConfig {
    return {
      label: this.translocoService.translate("account-setting.active_pull_stats"),
      color: "gray",
      variant: "solid",
    };
  }
}
